import { InvoicePlan, InvoicePlanStatus } from '@wonderschool/common-base-types';
import type { TFunction } from 'i18next';
import { getDateEnd, getInvoicePlanStatus, getProjectedCharges } from '../../../../helpers/invoicePlans';
import { InvoiceStatus } from '../../../../helpers/invoices';

const LEGACY_REFUNDED_STATUS = 'refunded';

export type EndPlanTiming = 'immediately' | 'specificDate';

export type EndPlanChargePreview = {
  invoiceCount: number;
  chargeAmount: number;
};

export type ExistingPlanInvoice = {
  status?: string;
  total?: number;
  paidAmount?: number;
  dateDue?: number | null;
};

export type EndPlanNotificationRequest = {
  subject: string;
  message: string;
  chargeAmount: number;
  invoiceCount: number;
  requestedAt: number;
};

export type EndPlanUpdate = Pick<InvoicePlan, 'dateEnd' | 'status'> & {
  endPlanNotification?: EndPlanNotificationRequest | null;
};

export function getEndPlanEmailCopy({
  t,
  parentName,
  studentName,
  schoolName,
  endDate,
  chargeAmount,
  invoiceCount,
  familyPaysProcessingFees = false,
  isChange = false,
  isImmediate = false,
}: {
  t: TFunction;
  parentName: string;
  studentName: string;
  schoolName: string;
  endDate: string;
  chargeAmount: string;
  invoiceCount: number;
  familyPaysProcessingFees?: boolean;
  isChange?: boolean;
  isImmediate?: boolean;
}): { subject: string; message: string } {
  const invoiceDescription = t('recurringPlanList.endPlan.emailInvoiceCount', { count: invoiceCount });
  const chargeCopy =
    invoiceCount > 0
      ? [
          t('recurringPlanList.endPlan.emailChargeSummary', { chargeAmount, invoiceDescription }),
          familyPaysProcessingFees ? t('recurringPlanList.endPlan.emailProcessingFeeNotice') : '',
        ]
          .filter(Boolean)
          .join(' ')
      : t('recurringPlanList.endPlan.emailNoRemainingCharges');

  const subject = isImmediate
    ? t('recurringPlanList.endPlan.emailSubjectImmediate', { studentName })
    : t(
        isChange ? 'recurringPlanList.endPlan.emailSubjectChanged' : 'recurringPlanList.endPlan.emailSubjectScheduled',
        { studentName, endDate }
      );
  const intro = isImmediate
    ? t('recurringPlanList.endPlan.emailIntroImmediate', { schoolName, studentName, endDate })
    : t(isChange ? 'recurringPlanList.endPlan.emailIntroChanged' : 'recurringPlanList.endPlan.emailIntroScheduled', {
        schoolName,
        studentName,
        endDate,
      });

  return {
    subject,
    message: t('recurringPlanList.endPlan.emailBody', { parentName, intro, chargeCopy, schoolName }),
  };
}

export function hasScheduledPlanEnd(invoicePlan: InvoicePlan, now = Date.now()): boolean {
  return !!invoicePlan.dateEnd && invoicePlan.dateEnd > now && invoicePlan.status !== InvoicePlanStatus.ENDED;
}

export function getEndPlanChargePreview({
  invoicePlan,
  timing,
  endDate,
  providerTimezone,
  existingInvoices = [],
}: {
  invoicePlan: InvoicePlan;
  timing: EndPlanTiming;
  endDate: string;
  providerTimezone: string;
  existingInvoices?: ExistingPlanInvoice[];
}): EndPlanChargePreview | null {
  const outstandingInvoices = existingInvoices.filter((invoice) => {
    const outstandingAmount = Math.max((invoice.total ?? 0) - (invoice.paidAmount ?? 0), 0);
    return (
      ![InvoiceStatus.PAID, InvoiceStatus.VOID, InvoiceStatus.UNCOLLECTIBLE, LEGACY_REFUNDED_STATUS].includes(
        invoice.status ?? ''
      ) && outstandingAmount > 0
    );
  });
  const outstandingAmount = outstandingInvoices.reduce(
    (total, invoice) => total + Math.max((invoice.total ?? 0) - (invoice.paidAmount ?? 0), 0),
    0
  );

  if (timing === 'immediately' || invoicePlan.status === InvoicePlanStatus.PAUSED) {
    return { invoiceCount: outstandingInvoices.length, chargeAmount: outstandingAmount };
  }

  if (invoicePlan.dateDue == null || typeof invoicePlan.total !== 'number') return null;

  const selectedEndDate = getDateEnd(endDate, providerTimezone);
  if (!selectedEndDate) return null;
  const projectedCharges = getProjectedCharges({
    perChargeAmount: invoicePlan.total,
    billingInterval: invoicePlan.billingInterval,
    dateStart: invoicePlan.dateStart,
    nextDueDate: invoicePlan.dateDue,
    dateEnd: selectedEndDate,
    serviceDirection: invoicePlan.serviceDirection,
    createdFromEnrollment: Boolean(
      (invoicePlan as InvoicePlan & { createdFromEnrollment?: boolean }).createdFromEnrollment
    ),
    timezone: providerTimezone,
    enrollmentBillingInterval: (invoicePlan.students[0] as any)?.enrollment?.tuitionAndFees?.tuition?.billingInterval,
  });
  if (!projectedCharges) return null;

  // The worker creates one full invoice for each plan student on every cycle.
  // Already-issued invoices above are already individual documents and must not
  // be multiplied again.
  const studentCount = invoicePlan.students.length;

  return {
    invoiceCount: outstandingInvoices.length + projectedCharges.count * studentCount,
    chargeAmount: outstandingAmount + projectedCharges.total * studentCount,
  };
}

export function getEndPlanUpdate({
  invoicePlan,
  timing,
  endDate,
  providerTimezone,
  now = Date.now(),
}: {
  invoicePlan: InvoicePlan;
  timing: EndPlanTiming;
  endDate: string;
  providerTimezone: string;
  now?: number;
}): EndPlanUpdate | null {
  if (![InvoicePlanStatus.ACTIVE, InvoicePlanStatus.PAUSED].includes(invoicePlan.status)) return null;

  const dateEnd = timing === 'immediately' ? now : getDateEnd(endDate, providerTimezone);
  if (!dateEnd) return null;
  if (timing === 'specificDate' && dateEnd < now) return null;

  const status = getInvoicePlanStatus(invoicePlan.dateDue, dateEnd, { now, currentStatus: invoicePlan.status });

  return { dateEnd, status };
}
